import { useState, type ReactElement } from 'react';
import type { PostStatusDto } from '@gr/core/content/posts/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { moveItem } from '../../../shared/collections';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { SelectField } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { useBlog } from '../../accounts/workspaces';
import { replacementFor } from './model/status-form';
import { NewStatusForm } from './NewStatusForm/NewStatusForm';
import { StatusItem } from './StatusItem/StatusItem';
import styles from './StatusesSection.module.scss';

type StatusesResult = OperationResult<PostStatusDto[]>;

// POST-UC-9, POST-UC-9.CC-1
export function StatusesSection(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog } = useBlog();
  const statuses = useResource(() => api.posts.statuses(blog.id), [api, blog.id]);
  const [deleting, setDeleting] = useState<PostStatusDto | null>(null);
  const change = useAction(async (action: () => Promise<StatusesResult>) => action());

  const run = async (action: () => Promise<StatusesResult>): Promise<StatusesResult> => {
    const result = await change.run(action);
    if (result.success) statuses.setData(result.data);
    return result;
  };

  const list = statuses.data ?? [];
  const reorder = (index: number, delta: number): void => void run(() => api.posts.reorderStatuses(blog.id, moveItem(list, index, delta).map((s) => s.id)));

  return (
    <Card title={t('posts.statuses.title')}>
      <div className="u-stack">
        <p className="u-muted u-small">{t('posts.statuses.subtitle')}</p>
        {change.error ? <Alert tone="danger">{te(change.error)}</Alert> : null}
        {statuses.loading && !statuses.data ? (
          <Loading label={t('common.loading')} />
        ) : (
          <ul className={styles.list} aria-label={t('posts.statuses.title')}>
            {list.map((status, index) => (
              <StatusItem
                key={status.id}
                status={status}
                disabled={change.pending}
                onSave={(input) => run(() => api.posts.updateStatus(blog.id, status.id, input))}
                onDelete={() => setDeleting(status)}
                onUp={index > 0 ? () => reorder(index, -1) : undefined}
                onDown={index < list.length - 1 ? () => reorder(index, 1) : undefined}
              />
            ))}
          </ul>
        )}
        <NewStatusForm
          onSubmit={async (values) => {
            const result = await api.posts.createStatus(blog.id, values);
            if (result.success) statuses.setData(result.data);
            return result;
          }}
        />
      </div>
      {deleting ? (
        <DeleteStatusDialog
          status={deleting}
          all={list}
          error={change.error ? te(change.error) : null}
          onClose={() => setDeleting(null)}
          onDelete={async (replaceWith) => {
            if ((await run(() => api.posts.deleteStatus(blog.id, deleting.id, replaceWith))).success) setDeleting(null);
          }}
        />
      ) : null}
    </Card>
  );
}

/** POST-UC-9.CC-1: удалить статус; если у него есть посты — выбрать, на какой их перевести. */
function DeleteStatusDialog({ status, all, error, onClose, onDelete }: { status: PostStatusDto; all: readonly PostStatusDto[]; error: string | null; onClose(): void; onDelete(replaceWith: string | undefined): Promise<void> }): ReactElement {
  const { t } = useT();
  const [replaceWith, setReplaceWith] = useState(() => replacementFor(status, all));
  const others = all.filter((s) => s.id !== status.id);
  return (
    <Dialog
      open
      title={t('posts.statuses.deleteTitle', { name: status.name })}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={() => void onDelete(status.postCount > 0 ? replaceWith : undefined)}>
            {t('common.delete')}
          </Button>
        </>
      }
    >
      <div className="u-stack">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <p>{t('posts.statuses.count', { n: status.postCount })}</p>
        {status.postCount > 0 ? (
          <SelectField
            label={t('posts.statuses.replaceWith')}
            value={replaceWith}
            options={others.map((s) => ({ value: s.id, label: `${s.name} · ${t(`posts.meta.${s.meta}`)}` }))}
            onChange={(e) => setReplaceWith(e.target.value)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
